<template>
  <SfModalPortal
    :content-padding="0"
    :footer-padding="0"
    :distance-from-bottom="safeDistanceFromBottom"
    :scrollable-content="true"
    footer-align="stretch"
    modal-id="adjustments-modal"
    size="wide"
    @close="clearAdjustmentId"
    @opened="handleOpen"
  >
    <template #title>
      <SfSkeletonLoader v-if="isLoading" class="tw-w-40 tw-h-5" />
      <span v-else>{{ displayTitle }}</span>
    </template>

    <template #messages>
      <SfModalMessage v-if="serverErrors.length > 0" variant="error">
        <div class="tw-max-h-20 tw-overflow-y-auto">
          <p v-for="error in serverErrors" :key="error" class="tw-m-0">
            {{ error }}
          </p>
        </div>
      </SfModalMessage>
    </template>

    <template #body>
      <Body :mode="mode" />
    </template>

    <template v-if="mode !== 'show'" #footer>
      <Footer
        :adjustment-id="adjustmentId"
        :all-tags="allTags"
        :categories="categories"
        :delete-url="deleteUrl"
        :is-loading="isLoading"
        :ledger-id="storedLedgerId"
        :mode="mode ?? 'new'"
        :permanent-entry-id="permanentEntryId"
        :purpose="purpose"
        :reconciliation-text-id="reconciliationTextId"
        :referer="referer"
        :save-url="saveUrl"
        :tags-add-url="tagsAddUrl"
        :tags-delete-url="tagsDeleteUrl"
        :title="title"
      />
    </template>
  </SfModalPortal>
</template>

<script setup lang="ts">
import type {
  AdjustmentModalPayload,
  AdjustmentModalNewPayload,
  AdjustmentModalEditPayload,
  AdjustmentModalBuildPayload,
} from '@app/types'
import type { AdjustmentCategory, Column, Tag } from '../types'
import { isEditAdjustmentResponse } from '../predicates'

import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'

import Body from './adjustment-modal-body.vue'
import Footer from './adjustments-modal-footer.vue'
import {
  buildAdjustment,
  editAdjustment,
  newAdjustment,
} from '@app/application/adjustments/api'
import { isUsingNewLayout } from '@app/adapters/silverfin'
import { SfLog } from '@app/adapters/logger'
import SfModalPortal from '@app/shared/vue/components/sf-modal/sf-modal-portal.vue'
import SfModalMessage from '@app/shared/vue/components/sf-modal/sf-modal-message.vue'
import SfSkeletonLoader from '@app/shared/vue/components/sf-skeleton-loader.vue'
import { useDeepLinking } from '@app/shared/vue/composables/use-deep-linking'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { getSafeJSONValue, removeJSONValue } from '@app/utils/local-storage'

import { useAdjustmentsModalStore } from '@app/application/adjustments/store/adjustment-modal'

defineOptions({
  name: 'AdjustmentsModal',
})

interface Props {
  accountEntryId?: number
  allTags: Tag[]
  categories: AdjustmentCategory[]
  columns: Column[]
  ledgerId?: number
  periodName?: string
  referer?: string
}

const props = withDefaults(defineProps<Props>(), {
  accountEntryId: undefined,
  allTags: () => [],
  ledgerId: 0,
  periodName: '',
  referer: '',
})

const I18n = useI18n()
const modalStore = useAdjustmentsModalStore()

const {
  addEmptySubAdjustment,
  receiveAttachments,
  receiveCategories,
  receiveColumns,
  receiveCompanyConfig,
  receiveSubAdjustments,
  receiveTags,
  resetStore,
} = modalStore

const {
  accountEntryId: storedAccountEntryId,
  isLoading,
  isLocked,
  lastOpenedAdjustmentId,
  ledgerId: storedLedgerId,
  mode,
  serverErrors,
  subAdjustmentOrder,
  subAdjustments: existingSubAdjustments,
} = storeToRefs(modalStore)

onMounted(() => {
  checkForLingeringModal()
})

receiveColumns(props.columns)
storedLedgerId.value = props.ledgerId
storedAccountEntryId.value = props.accountEntryId

const [adjustmentId, clearAdjustmentId] = useDeepLinking<string | null>(null, {
  key: 'adjustment_id',
  shouldClearOnEmpty: true,
})
const permanentEntryId = ref<number | null>(null)
const purpose = ref('')
const reconciliationTextId = ref<number | null>(null)
const saveUrl = ref('')
const deleteUrl = ref('')
const tagsAddUrl = ref('')
const tagsDeleteUrl = ref('')
const title = ref('')
const openedForDifferentLedger = ref(false)

const displayTitle = computed(() => {
  if (
    !isUsingNewLayout() ||
    !props.periodName ||
    !title.value ||
    openedForDifferentLedger.value
  ) {
    return title.value
  }

  return I18n.t('adjustment.modal.title_with_period', {
    title: title.value,
    period: props.periodName,
  })
})

function checkForLingeringModal() {
  const payload =
    getSafeJSONValue<AdjustmentModalNewPayload | AdjustmentModalEditPayload>(
      'adjustments-modal'
    )

  if (payload) {
    // If Add+New was previously clicked
    removeJSONValue('adjustments-modal')

    // The payload was stashed from whatever ledger the user was on at the
    // time; discard it if it doesn't match the ledger this mount is on, so
    // it doesn't resurrect on an unrelated page/period.
    if (payload.ledgerId === props.ledgerId) {
      window.modalCitizen.openModalPortal('adjustments-modal', payload)
    }
  } else if (adjustmentId.value) {
    // If the modal was opened from a deep link
    window.modalCitizen.openModalPortal('adjustments-modal', {
      adjustmentId: adjustmentId.value,
      mode: 'edit',
    })
  }
}

function handleOpen(payload: AdjustmentModalPayload) {
  openedForDifferentLedger.value =
    payload.mode !== 'build' &&
    !!payload.ledgerId &&
    payload.ledgerId !== props.ledgerId

  if (payload.mode !== 'build' && payload.ledgerId) {
    storedLedgerId.value = payload.ledgerId
  }

  if (
    (payload.mode === 'edit' || payload.mode === 'show') &&
    payload.adjustmentId !== lastOpenedAdjustmentId.value
  ) {
    resetStore()
    mode.value = payload.mode
    handleOpenEdit(payload.adjustmentId)
  }

  if (payload.mode === 'new') {
    if (mode.value !== 'new') {
      resetStore()
      addEmptySubAdjustment()
      isLoading.value = true
    }

    handleOpenNew(payload.accountEntryId)
  }

  if (payload.mode === 'build') {
    resetStore()
    handleOpenBuild(payload)
  }

  const nextAdjustmentId =
    payload.mode === 'edit' || payload.mode === 'show'
      ? payload.adjustmentId
      : ''
  adjustmentId.value = nextAdjustmentId
  lastOpenedAdjustmentId.value = nextAdjustmentId
}

async function handleOpenEdit(adjustmentId: string): Promise<void> {
  isLoading.value = true

  try {
    const response = await editAdjustment({
      adjustmentId,
      ledgerId: storedLedgerId.value,
    })

    if (!response) {
      throw new Error('No response')
    }

    if (!response?.permissions?.edit && response?.permissions?.show) {
      mode.value = 'show'
    }

    const locked =
      mode.value === 'show' ||
      (!response?.permissions?.change && response?.permissions?.edit)
    isLocked.value = locked
    receiveAttachments(response.documents)
    receiveCategories(response.category)
    receiveCompanyConfig(response.companyConfig)
    receiveSubAdjustments(response.subAdjustments, {
      shouldCalculateBalance: true,
    })

    if (!locked) {
      addEmptySubAdjustment()
    }

    const reconTextId = response.reconciliationTextId

    if (reconTextId) {
      // The adjustment is already associated with a reconciliation text.
      // Clear any stored account entry id on it to prevent multiple polymorphic associations.
      // This can happen when an adjustment is first made on a reconcliation text, associated with an account
      // and then opened through the account entry page.
      storedAccountEntryId.value = undefined
    }

    receiveTags({ previousTags: response.previousTags })
    permanentEntryId.value = response.permanentEntryId
    purpose.value = response.purpose
    reconciliationTextId.value = reconTextId
    saveUrl.value = response.saveUrl
    deleteUrl.value = response.deleteUrl || ''
    tagsAddUrl.value = response.tagsAddUrl
    tagsDeleteUrl.value = response.tagsDeleteUrl
    title.value = response.title
  } catch (e) {
    logError('Error editing', e)
  } finally {
    isLoading.value = false
  }
}

async function handleOpenNew(
  accountEntryId: number | undefined
): Promise<void> {
  if (accountEntryId) {
    storedAccountEntryId.value = accountEntryId
  }

  deleteUrl.value = ''

  try {
    const response = await newAdjustment({
      accountEntryId: storedAccountEntryId.value,
      ledgerId: storedLedgerId.value,
    })

    if (!response) {
      throw new Error('No response')
    }

    const firstAdjustmentWithAccount = response.subAdjustments.find(
      (adjustment) => !!adjustment?.account?.accountId
    )

    // In case we want to prefill the first row with an account, e.g. coming from an account entry page
    if (firstAdjustmentWithAccount) {
      const firstSubAdjustmentId = subAdjustmentOrder.value[0]
      const firstEmptySubAdjustment =
        existingSubAdjustments.value[firstSubAdjustmentId]

      firstEmptySubAdjustment.account = {
        ...firstAdjustmentWithAccount.account,
        id: firstAdjustmentWithAccount.account.accountId,
        actualValue: parseFloat(
          firstAdjustmentWithAccount.account.actualValue ?? 0
        ),
      }
    }

    permanentEntryId.value = response.permanentEntryId
    purpose.value = response.purpose
    reconciliationTextId.value = response.reconciliationTextId
    saveUrl.value = response.saveUrl
    title.value = response.title
    mode.value = 'new'
    receiveCategories(response.category || props.categories?.[0][1])
    receiveCompanyConfig(response.companyConfig)
  } catch (e) {
    logError('Error creating new', e)
  } finally {
    isLoading.value = false
  }
}

async function handleOpenBuild(payload: AdjustmentModalBuildPayload) {
  isLoading.value = true

  try {
    const response = await buildAdjustment({
      data: payload.data,
      ledgerId: storedLedgerId.value,
    })

    if (!response) {
      throw new Error('No response')
    }

    if (isEditAdjustmentResponse(response)) {
      receiveAttachments(response.documents)
      receiveCategories(response.category)
      receiveTags({ previousTags: response.previousTags })
      tagsAddUrl.value = response.tagsAddUrl
      tagsDeleteUrl.value = response.tagsDeleteUrl
    } else {
      receiveCategories(response.category || props.categories?.[0][1])
    }

    permanentEntryId.value = response.permanentEntryId
    purpose.value = response.purpose
    reconciliationTextId.value = response.reconciliationTextId
    saveUrl.value = response.saveUrl
    title.value = response.title
    mode.value = response.mode
    receiveCompanyConfig(response.companyConfig)
    receiveSubAdjustments(response.subAdjustments, {
      shouldCalculateBalance: false,
    })
    addEmptySubAdjustment()
  } catch (e) {
    logError('Error building', e)
  } finally {
    isLoading.value = false
  }
}

function logError(message: string, e: unknown): void {
  SfLog.warn(`Adjustments::${message}`, {
    extra: {
      firmId: window.Silverfin.currentFirmId,
      companyId: window.Silverfin.currentCompanyId,
      ledgerId: storedLedgerId.value,
      error: e,
    },
  })
}

// Each option is 30px high, so let's allow for 4 options to be visible at any given time
const safeDistanceFromBottom = computed(() => {
  return (
    Math.min(Math.max(props.categories.length, props.allTags.length), 4) * 30
  )
})
</script>
